import { Autocomplete, Chip, createFilterOptions, TextField } from '@mui/material';
import type { ReactElement } from 'react';
import type { IAutocompleteProps, ILovDetail } from '../../../types';

/**
 * Renders an input field to handle multiple options selections
 * @param {IAutocompleteProps} props AutoComplete Props
 * @returns {React.JSX.Element} JSX element
 */
export const MultiAutocompleteInput = (props: IAutocompleteProps): ReactElement => {
  const handleInputChange = (newVal): void => {
    const val =
      newVal?.length > 0 ? JSON.stringify(newVal.map((x) => (x.inValid ? x.name : x.code))) : null;
    props.handleChange(props.code, val);
  };
  const hasInvalidValue = Array.isArray(props.value) && props.value.some((item) => item.inValid);

  return (
    <Autocomplete
      multiple
      id={props.code + '-multi-autocomplete'}
      data-testid="multi-autocomplete"
      size={props.size}
      options={props.data as ILovDetail[]}
      onChange={(_, newVal): void => handleInputChange(newVal)}
      getOptionLabel={(option): string => option.name}
      filterSelectedOptions
      disabled={props.disabled}
      value={props.value ? (props.value as ILovDetail[]) : []}
      className={hasInvalidValue ? 'autocomplete-value-mismatch' : undefined}
      renderInput={(params): ReactElement => (
        <TextField
          {...params}
          label={props.label}
          error={!!hasInvalidValue}
          slotProps={{
            ...params.slotProps,
            inputLabel: {
              ...params.slotProps?.inputLabel,
              className: props.isRequired ? 'required' : undefined,
            },
          }}
        />
      )}
      filterOptions={createFilterOptions({ trim: true })} // Create a custom filter that trims whitespace
      renderValue={(value, getTagProps): ReactElement[] =>
        value.map((option, index: number) => (
          <Chip
            variant="outlined"
            label={option.name}
            {...(getTagProps ? getTagProps({ index }) : {})}
            key={option.code}
            color={option.inValid ? 'error' : 'default'}
          />
        ))
      }
    />
  );
};
